import { DateTime } from "luxon";
import { isGateCohort } from "../pdrGateHelpers";

export type RequirementHistoryRowContext = {
  pdpSerial: string;
  cohortCode: string;
  cycleStartsOn: string;
  cycleEndsOn: string;
  requirementStartDate: string;
  requirementEndDate?: string | null;
  evaluationStartDate?: string | null;
  evaluationEndDate?: string | null;
};

export type UpdateRequirementDatesFormValues = {
  requirementStartDate: string;
  requirementEndDate: string;
  evaluationStartDate: string;
  evaluationEndDate: string;
};

function parseDate(value: string | null | undefined): DateTime | null {
  if (!value) return null;
  const parsed = DateTime.fromISO(value, { zone: "utc" }).startOf("day");
  return parsed.isValid ? parsed : null;
}

function dateRangesOverlap(startA: DateTime, endA: DateTime, startB: DateTime, endB: DateTime): boolean {
  return startA <= endB && startB <= endA;
}

export function validateUpdateRequirementDates(
  values: UpdateRequirementDatesFormValues,
  row: RequirementHistoryRowContext,
  siblingRows: RequirementHistoryRowContext[]
): string | null {
  const requirementStart = parseDate(values.requirementStartDate);
  const requirementEnd = parseDate(values.requirementEndDate);
  const evaluationStart = parseDate(values.evaluationStartDate);
  const evaluationEnd = parseDate(values.evaluationEndDate);
  const cycleStart = parseDate(row.cycleStartsOn);
  const cycleEnd = parseDate(row.cycleEndsOn);
  const gateCohort = isGateCohort(row.cohortCode);

  if (!requirementStart || !requirementEnd || !evaluationStart || !evaluationEnd || !cycleStart || !cycleEnd) {
    return "All four dates are required.";
  }

  if (requirementStart > requirementEnd) {
    return "Requirement start date must be on or before requirement end date.";
  }

  if (evaluationStart > evaluationEnd) {
    return "Evaluation start date must be on or before evaluation end date.";
  }

  if (!gateCohort && requirementStart < cycleStart) {
    return `Requirement start date cannot be before the cycle start (${cycleStart.toFormat("MM/dd/yyyy")}).`;
  }

  if (requirementEnd > cycleEnd) {
    return `Requirement end date cannot be after the cycle end (${cycleEnd.toFormat("MM/dd/yyyy")}).`;
  }

  if (!gateCohort && evaluationStart < cycleStart) {
    return `Evaluation start date cannot be before the cycle start (${cycleStart.toFormat("MM/dd/yyyy")}).`;
  }

  if (evaluationEnd > cycleEnd) {
    return `Evaluation end date cannot be after the cycle end (${cycleEnd.toFormat("MM/dd/yyyy")}).`;
  }

  for (const sibling of siblingRows) {
    if (sibling.pdpSerial === row.pdpSerial) continue;

    const siblingStart = parseDate(sibling.requirementStartDate);
    const siblingEnd = parseDate(sibling.requirementEndDate || row.cycleEndsOn);
    if (!siblingStart || !siblingEnd) continue;

    if (dateRangesOverlap(requirementStart, requirementEnd, siblingStart, siblingEnd)) {
      return `Requirement dates overlap another requirement row (${siblingStart.toFormat("MM/dd/yyyy")} - ${siblingEnd.toFormat("MM/dd/yyyy")}).`;
    }
  }

  return null;
}
